import { ReactNode, useCallback, MouseEvent, useState } from 'react';
import { useUser, useSetAlert, useProcessTemplateMutation, useOrganizationQuery } from '@tymely/services';
import { Allotment } from 'allotment';
import { Button, styled, Box, CircularProgress, Card, Tabs, Tab } from '@mui/material';
import ForwardIcon from '@mui/icons-material/Forward';
import { useNavigate } from 'react-router-dom';
import { useHistoryStack } from '@tymely/utils';
import TemplateViewer from '@tymely/features/ContentEditor/TemplateViewer';
import { IOrganizationConfig, IPolicySet } from '@tymely/atoms';
import { IOrganization } from '@tymely/atoms';
import { BaseModal } from '@tymely/components';
import { TemplateEditorMode } from '@global/types';
import { TemplateEditorModeProvider, useTemplateEditorMode } from '@tymely/features';
import { stopProgagation, findActualResponse } from '@tymely/utils';
import usePolicyParams from '@tymely/pages/GraphPolicyEditor/hooks/usePolicyParams';

import TemplateCodeEditor from './TemplateCodeEditor';
import TemplateSideBar from './TemplateSideBar';
import TemplateEditorProvider, { TemplateEditorProviderProps } from './TemplateEditorProvider';
import TemplateViewers from './TemplateViewers';
import { useTemplateEditor } from './TemplateEditorProvider';

import 'allotment/dist/style.css';

const _BackButton = styled(Button)`
    border-radius: 0;
    .MuiButton-startIcon {
        transform: rotate(-180deg);
    }
`;

const CenteredBox = styled(Box)({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary',
});

const BackButton = () => {
    const navigations = useHistoryStack();
    const navigate = useNavigate();

    if (navigations.length <= 0) {
        return null;
    }

    return (
        <_BackButton fullWidth variant="contained" startIcon={<ForwardIcon />} onClick={() => navigate(-1)}>
            Back
        </_BackButton>
    );
};

type TemplateEditorProps = TemplateEditorProviderProps & {
    withBackButton?: boolean;
    onModeChange?: (event: MouseEvent<HTMLElement>, newMode: TemplateEditorMode) => void;
    organizationId?: IOrganization['id'];
    intentId?: IPolicySet['id'];
};

const TemplateEditor = ({ withBackButton, children, organizationId, intentId, ...rest }: TemplateEditorProps) => {
    const user = useUser();
    const { data: org } = useOrganizationQuery(organizationId!, !!organizationId);

    if (!user?.isAdmin) {
        return <div>Access denied</div>;
    }

    return (
        <TemplateEditorModeProvider initialMode="template">
            <TemplateEditorProvider organizationId={organizationId} intentId={intentId} {...rest}>
                <TemplateEditorContent
                    markupLanguage={org?.config?.response_markup_language}
                    withBackButton={withBackButton}
                >
                    {children}
                </TemplateEditorContent>
            </TemplateEditorProvider>
        </TemplateEditorModeProvider>
    );
};

export const TemplateEditorModal = (props: {
    open: boolean;
    template: string;
    onSave?: (value: string) => void;
    onClose?: () => void;
}) => {
    const [template, setTemplate] = useState<string>(props.template);

    const onSave = () => {
        if (template !== undefined) {
            props.onSave?.(template);
            props.onClose?.();
        }
    };

    return (
        <BaseModal open={props.open} maxWidth="xl" fullHeight onOk={onSave} onClose={props.onClose}>
            <TemplateEditorModeProvider initialMode="template">
                <TemplateEditor template={template} onChange={setTemplate} />
            </TemplateEditorModeProvider>
        </BaseModal>
    );
};

const _TemplateViewer = (props: {
    value: string;
    markupLanguage?: IOrganizationConfig['response_markup_language'];
}) => (
    <Card
        onKeyDown={stopProgagation}
        sx={{
            height: '100%',
            padding: 2,
            overflow: 'scroll',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottomLeftRadius: 0,
        }}
    >
        <TemplateViewer markupLanguage={props.markupLanguage} template={props.value} />
    </Card>
);

const TemplateEditorContent = ({
    withBackButton,
    markupLanguage,
    children,
}: {
    withBackButton?: boolean;
    markupLanguage?: IOrganizationConfig['response_markup_language'];
    children?: ReactNode;
}) => {
    const showAlert = useSetAlert();
    const processTemplateMutation = useProcessTemplateMutation();
    const { renderTemplateQuery, comment, templateObj, ticketOfflineQuery } = useTemplateEditor();
    const { editorMode } = useTemplateEditorMode();
    const [processedResult, setProcessedResult] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const { organizationId } = usePolicyParams();
    const { data: org } = useOrganizationQuery(organizationId);

    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    }, []);

    const actualInquiry = ticketOfflineQuery.data?.comments.find((c) => c.id === comment?.[0]?.value);
    const actualResponse = findActualResponse(ticketOfflineQuery.data?.comments, actualInquiry, org as IOrganization);

    const handleRunClick = useCallback(async () => {
        if (!comment?.[0]?.value || !renderTemplateQuery.data) {
            showAlert('Please select a comment and ensure template is rendered', 'error');
            return;
        }

        try {
            setIsProcessing(true);
            setProcessedResult('');

            let templateType: string | undefined;
            if (editorMode === 'template') {
                templateType = 'RESPONSE_TEMPLATE_PERSONALIZATION';
            } else if (editorMode === 'chat') {
                templateType = 'CHAT_DEFAULT_GUIDELINES';
            } else {
                templateType = undefined;
            }

            const response = await processTemplateMutation.mutateAsync({
                response_txt: renderTemplateQuery.data,
                comment_id: comment[0].value,
                template_type: templateType,
            });
            setProcessedResult(response.replace(/\n/g, '<br />'));
            showAlert('Template processed successfully', 'success');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to process template';
            showAlert(errorMessage, 'error');
            console.error('Failed to process template:', error);
        } finally {
            setIsProcessing(false);
        }
    }, [processTemplateMutation, comment, renderTemplateQuery.dataUpdatedAt, showAlert, editorMode]);

    const shouldShowProcessingView = editorMode === 'prompt' || editorMode === 'chat' || !templateObj?.personalized;

    return (
        <Allotment>
            <Allotment.Pane preferredSize="25%" minSize={300}>
                <TemplateSideBar>{withBackButton && <BackButton />}</TemplateSideBar>
            </Allotment.Pane>
            <Allotment.Pane preferredSize="75%">
                <Allotment vertical>
                    <Allotment.Pane minSize={300}>
                        <TemplateCodeEditor>{children}</TemplateCodeEditor>
                        <TemplateViewers />
                    </Allotment.Pane>
                    <Allotment.Pane snap={true}>
                        {shouldShowProcessingView ? (
                            <Allotment>
                                <Allotment.Pane>
                                    <Box sx={{ position: 'relative', height: '100%' }}>
                                        <_TemplateViewer
                                            markupLanguage={markupLanguage}
                                            value={renderTemplateQuery.data ?? ''}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 16,
                                                right: 16,
                                                zIndex: 1,
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                onClick={handleRunClick}
                                                disabled={isProcessing}
                                                sx={{
                                                    bgcolor: 'warning.main',
                                                    '&:hover': {
                                                        bgcolor: 'warning.dark',
                                                    },
                                                }}
                                            >
                                                {isProcessing ? 'Processing...' : 'Run'}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Allotment.Pane>
                                {(isProcessing || processedResult) && (
                                    <Allotment.Pane>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                bgcolor: 'background.paper',
                                                position: 'relative',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Tabs
                                                value={activeTab}
                                                onChange={handleTabChange}
                                                sx={{
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    minHeight: 'auto',
                                                }}
                                            >
                                                <Tab label="Simulated Response" />
                                                <Tab label="Actual Response" />
                                            </Tabs>
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    p: 2,
                                                    overflow: 'auto',
                                                }}
                                            >
                                                {activeTab === 0 && (
                                                    <>
                                                        {isProcessing ? (
                                                            <CenteredBox>
                                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                                Processing template...
                                                            </CenteredBox>
                                                        ) : processedResult ? (
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: processedResult,
                                                                }}
                                                            />
                                                        ) : null}
                                                    </>
                                                )}
                                                {activeTab === 1 && (
                                                    <>
                                                        {actualResponse ? (
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html:
                                                                        actualResponse?.body_html ||
                                                                        actualResponse?.body ||
                                                                        '',
                                                                }}
                                                            />
                                                        ) : null}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    </Allotment.Pane>
                                )}
                            </Allotment>
                        ) : (
                            <_TemplateViewer markupLanguage={markupLanguage} value={renderTemplateQuery.data ?? ''} />
                        )}
                    </Allotment.Pane>
                </Allotment>
            </Allotment.Pane>
        </Allotment>
    );
};

export default TemplateEditor;
